import { splitActionKey } from '@shared/utils/splitActionKey';
import {
	LaytimeIntervals,
	LaytimeTerms,
} from '@shared/utils/constants';
import { CargoProps } from '@api/models/cargo';
import { CargoPortProps } from '@api/models/cargo-port';
import type { EditableCargo } from '@api/features/cargos/updateCargo';
import { round } from './math';
import { calculateTimeAllowed } from './laytimeStats/calculateTimeAllowed';
import {
	isLoadPort,
	isDischargePort,
} from './cargoPortHelpers';
import { convertIntervalToDays } from './laytime';
import normalize from './normalizeNumber';

type IntervalValue = {
	value: number | null | undefined;
	interval: LaytimeIntervals;
};

const firstDefinedInDays = (sources: Array<IntervalValue>): number => {
	const source = sources.find((s) => s.value != null);

	if (source == null) {
		return 0;
	}

	return convertIntervalToDays(normalize(source.value), source.interval);
};

const convertRateToMatchTimeInterval = (
	rate: number,
	rateInterval: LaytimeIntervals,
	timeInterval: LaytimeIntervals,
): number => {
	if (rateInterval === timeInterval) {
		return rate;
	}

	if (rateInterval === LaytimeIntervals.DAY && timeInterval === LaytimeIntervals.HOUR) {
		// Daily rate to hourly rate
		return rate / 24;
	}

	if (rateInterval === LaytimeIntervals.HOUR && timeInterval === LaytimeIntervals.DAY) {
		// Hourly rate to daily rate
		return rate * 24;
	}

	return rate;
};

const getTimeAllowed = ({
	cargo,
	cargoPort,
	cargoPorts,
	estimatedWorkingDaysOverride,
	recalculateTimeAllowed,
}: {
	cargo: Omit<CargoProps, 'loadingPorts' | 'dischargePorts'>;
	cargoPort: CargoPortProps;
	cargoPorts: Array<CargoPortProps>;
	recalculateTimeAllowed?: boolean;
	estimatedWorkingDaysOverride?: number | null;
}) => {
	const { laytimeTerms, timeAllowedInterval } = cargo;

	if (estimatedWorkingDaysOverride) {
		return estimatedWorkingDaysOverride;
	}

	if (recalculateTimeAllowed && laytimeTerms === LaytimeTerms.NON_REVERSIBLE) {
		const timeAllowed = calculateTimeAllowed({
			quantity: cargoPort.cpQuantity,
			rate: cargoPort.loadingRate,
			method: cargoPort.calculationMethod,
			prorate: true,
			cargoPort,
		});

		if (timeAllowed) {
			return convertIntervalToDays(timeAllowed, cargoPort.laytimeInterval);
		}

		return 0;
	}

	if (laytimeTerms === LaytimeTerms.REVERSIBLE) {
		const timeAllowedInDays = convertIntervalToDays(normalize(cargo.timeAllowed), timeAllowedInterval);

		return timeAllowedInDays / cargoPorts.length;
	}

	if (laytimeTerms === LaytimeTerms.AVERAGED) {
		const isLoad = isLoadPort(cargoPort);

		if (isLoad) {
			const loadPorts = cargoPorts.filter(isLoadPort);
			const timeAllowedInDays = convertIntervalToDays(normalize(cargo.timeAllowedLoading), timeAllowedInterval);

			return timeAllowedInDays / loadPorts.length;
		}

		const disPorts = cargoPorts.filter(isDischargePort);
		const timeAllowedInDays = convertIntervalToDays(normalize(cargo.timeAllowedDischarging), timeAllowedInterval);

		return timeAllowedInDays / disPorts.length;
	}

	return convertIntervalToDays(normalize(cargoPort?.estimatedTimeAllowed), cargoPort.laytimeInterval);
};

type Props = {
	cargo: CargoProps;
	cargoPort: CargoPortProps;
	cargoPorts: Array<CargoPortProps>;
	incomingCargoAttributes?: Partial<CargoProps | EditableCargo>;
	incomingCargoPortAttributes?: Partial<CargoPortProps>;
	estimatedWorkingDaysOverride?: number | null;
	isCopy?: boolean;
	recalculateTimeAllowedOverride?: boolean;
};

export const getCargoPortLaytimeEstimatedValues = ({
	cargo,
	incomingCargoAttributes = {},
	cargoPort,
	incomingCargoPortAttributes = {},
	cargoPorts,
	estimatedWorkingDaysOverride,
	isCopy,
	recalculateTimeAllowedOverride,
}: Props) => {
	const cargoToUse: Omit<CargoProps, 'loadingPorts' | 'dischargePorts'> = {
		...cargo,
		...incomingCargoAttributes,
	};

	const recalculateTimeAllowed = (
		!cargoPort.estimateDemurrage &&
		incomingCargoPortAttributes?.cpQuantity != null ||
		incomingCargoPortAttributes?.loadingRate != null ||
		incomingCargoPortAttributes?.laytimeInterval != null ||
		incomingCargoPortAttributes?.calculationMethod != null ||
		cargoPort.estimatedTimeAllowed != null ||
		incomingCargoPortAttributes?.timeAllowedOverride != null ||
		incomingCargoAttributes?.quantity != null ||
		incomingCargoAttributes.laytimeTerms != null ||
		incomingCargoAttributes?.demurrageDespatchInterval != null ||
		recalculateTimeAllowedOverride === true
	);

	const { action } = splitActionKey(cargoPort.portAndActionKey);
	let estimatedDemurrage = null;
	let estimatedDespatch = null;
	let estimatedTimeUsed = 0;

	let rawDemurrageRate: number;
	let rawDespatchRate: number;
	let rateInterval: LaytimeIntervals;

	const { timeAllowedInterval } = cargoToUse;
	const { laytimeInterval } = cargoPort;

	switch (cargoToUse.laytimeTerms) {
		case LaytimeTerms.NON_REVERSIBLE:
			rawDemurrageRate = normalize(cargoPort.demurrage);
			rawDespatchRate = normalize(cargoPort.despatch);
			estimatedTimeUsed = firstDefinedInDays([
				{ value: incomingCargoAttributes.estimatedTimeUsed, interval: timeAllowedInterval },
				{ value: incomingCargoAttributes.timeAllowed, interval: timeAllowedInterval },
				{ value: cargoPort.estimatedTimeUsed, interval: laytimeInterval },
			]);
			rateInterval = cargoToUse.demurrageDespatchInterval;
			break;

		case LaytimeTerms.REVERSIBLE:
			rawDemurrageRate = normalize(cargoToUse.demurrage);
			rawDespatchRate = normalize(cargoToUse.despatch);
			estimatedTimeUsed = firstDefinedInDays([
				{ value: incomingCargoAttributes.estimatedTimeUsed, interval: timeAllowedInterval },
				{ value: incomingCargoAttributes.timeAllowed, interval: timeAllowedInterval },
				{ value: cargoToUse.estimatedTimeUsed, interval: timeAllowedInterval },
			]) / cargoPorts.length;

			rateInterval = cargoToUse.demurrageDespatchInterval;
			break;

		default:
		case LaytimeTerms.AVERAGED:
			if (action === 'loading') {
				const loadPorts = cargoPorts.filter(isLoadPort);

				rawDemurrageRate = normalize(cargoToUse.demurrageLoading);
				rawDespatchRate = normalize(cargoToUse.despatchLoading);
				estimatedTimeUsed = firstDefinedInDays([
					{ value: incomingCargoAttributes.estimatedTimeUsedLoading, interval: timeAllowedInterval },
					{ value: incomingCargoAttributes.timeAllowedLoading, interval: timeAllowedInterval },
					{ value: cargoToUse.estimatedTimeUsedLoading, interval: timeAllowedInterval },
				]) / loadPorts.length;
			} else {
				const disPorts = cargoPorts.filter(isDischargePort);

				rawDemurrageRate = normalize(cargoToUse.demurrageDischarge);
				rawDespatchRate = normalize(cargoToUse.despatchDischarge);
				estimatedTimeUsed = firstDefinedInDays([
					{ value: incomingCargoAttributes.estimatedTimeUsedDischarging, interval: timeAllowedInterval },
					{ value: incomingCargoAttributes.timeAllowedDischarging, interval: timeAllowedInterval },
					{ value: cargoToUse.estimatedTimeUsedDischarging, interval: timeAllowedInterval },
				]) / disPorts.length;
			}

			rateInterval = cargoToUse.demurrageDespatchInterval;
			break;
	}

	const demurrageRate = convertRateToMatchTimeInterval(rawDemurrageRate, rateInterval, LaytimeIntervals.DAY);
	const despatchRate = convertRateToMatchTimeInterval(rawDespatchRate, rateInterval, LaytimeIntervals.DAY);

	const estimatedTimeAllowed = getTimeAllowed({
		cargo: cargoToUse,
		cargoPort,
		cargoPorts,
		estimatedWorkingDaysOverride,
		recalculateTimeAllowed,
	});

	if (recalculateTimeAllowed && !cargoPort.estimateDemurrage) {
		estimatedTimeUsed = estimatedTimeAllowed;
	}

	const workingDays = normalize(estimatedTimeAllowed);

	const idleDays = normalize(cargoPort.idleDays);
	const laytimeLostOrGained = estimatedTimeUsed ? workingDays - estimatedTimeUsed : 0;

	const totalTimeInPort = normalize(estimatedTimeUsed) + normalize(cargoPort.idleDays);

	// Despatch - you're estimated to use LESS time than allowed
	if (laytimeLostOrGained > 0) {
		estimatedDespatch = round(laytimeLostOrGained * (despatchRate ?? 0), 6);
	}

	// Demurrage - you're estimated to use MORE time than allowed
	if (laytimeLostOrGained < 0) {
		estimatedDemurrage = round(laytimeLostOrGained * (demurrageRate ?? 0), 6);
	}

	if (cargoToUse.laytimeTerms !== LaytimeTerms.NON_REVERSIBLE) {
		estimatedDemurrage = 0;
		estimatedDespatch = 0;
	}

	const {
		demurrage: cargoDemurrage,
		demurrageLoading: cargoDemurrageLoading,
		demurrageDischarge: cargoDemurrageDischarge,
		despatch: cargoDespatch,
		despatchLoading: cargoDespatchLoading,
		despatchDischarge: cargoDespatchDischarge,
	} = incomingCargoAttributes ?? {};

	const shouldUpdate = (
		cargoDemurrage || cargoDemurrageLoading || cargoDemurrageDischarge ||
		cargoDespatch || cargoDespatchLoading || cargoDespatchDischarge
	) && !isCopy;

	let { demurrage } = cargoPort;
	let { despatch } = cargoPort;

	if (shouldUpdate) {
		const isLoading = isLoadPort(cargoPort);
		const isDischarging = isDischargePort(cargoPort);

		if (cargoDemurrage) {
			demurrage = cargoDemurrage;
			despatch = cargoDemurrage / 2;
		}

		if (cargoDespatch) {
			despatch = cargoDespatch;
		}

		if (cargoDemurrageLoading && isLoading) {
			demurrage = cargoDemurrageLoading;
			despatch = cargoDemurrageLoading / 2;
		}

		if (cargoDemurrageDischarge && isDischarging) {
			demurrage = cargoDemurrageDischarge;
			despatch = cargoDemurrageDischarge / 2;
		}

		if (cargoDespatchLoading && isLoading) {
			despatch = cargoDespatchLoading;
		}

		if (cargoDespatchDischarge) {
			despatch = cargoDespatchDischarge;
		}
	}

	return {
		estimatedDemurrage,
		estimatedDespatch,
		estimatedTimeAllowed,
		totalTimeInPort,
		workingDays,
		idleDays,
		demurrage,
		despatch,
		estimatedTimeUsed: normalize(estimatedTimeUsed),
	};
};
